import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authService } from "../../services/auth";
import { decryptUserData, loadEncryptedUserData } from "../utils/configUtils";
import type { RootState } from "../store";
import type { UserInfo, UserLevelPermission } from "../../types/user";

export type { UserInfo };

interface AuthState {
  user: UserInfo;
  isAuthenticated: boolean;
  status: "idle" | "loading" | "succeeded" | "failed";
  error?: string;
}

const defaultUser: UserInfo = {
  name: "User",
  id: "000000",
  email: "user@example.com",
  role: "Guest",
  phone: "",
  branch: "Main Office",
  branch_id: null,
  branch_name: null,
  branch_switching: 0,
  userlevelpermissions: [],
  modulelevelpermissions: {},
};

const mapDecryptedDataToUser = (
  decryptedData: Record<string, any>,
  fallbackBranch?: string
): UserInfo => {
  // Extract branch_switching permission
  const branchSwitchingRaw =
    decryptedData.branch_switching ||
    decryptedData.branchSwitching ||
    decryptedData.branch_switch ||
    decryptedData.branchSwitch ||
    decryptedData.branch_switching_flag;

  let branch_switching: number = 0;
  if (typeof branchSwitchingRaw === "number") {
    branch_switching = branchSwitchingRaw;
  } else if (typeof branchSwitchingRaw === "string" && branchSwitchingRaw.trim() !== "") {
    const parsed = Number(branchSwitchingRaw);
    if (!Number.isNaN(parsed)) {
      branch_switching = parsed;
    }
  }

  // Extract branch_id
  const branch_id =
    decryptedData.branch_id ||
    decryptedData.branchId ||
    decryptedData.transaction_branch_id ||
    decryptedData.transactionBranchId ||
    null;

  // Extract branch_name
  const branch_name =
    decryptedData.branch_name ||
    decryptedData.branchName ||
    decryptedData.branch ||
    fallbackBranch ||
    null;

  // Extract display branch name
  const branch =
    decryptedData.branch_name ||
    decryptedData.branchName ||
    decryptedData.branch ||
    fallbackBranch ||
    defaultUser.branch;

  // Extract userlevelpermissions
  const rawPerms = decryptedData.userlevelpermissions;
  const userlevelpermissions: UserLevelPermission[] = Array.isArray(rawPerms)
    ? rawPerms.map((p: any) => ({
        Id: p.Id,
        ScreenId: String(p.ScreenId),
        UserId: p.UserId,
        Add: p.Add === true || p.Add === "true" || p.Add === "1" || p.Add === 1,
        Edit: p.Edit === true || p.Edit === "true" || p.Edit === "1" || p.Edit === 1,
        Delete: p.Delete === true || p.Delete === "true" || p.Delete === "1" || p.Delete === 1,
        Unlock: p.Unlock === true || p.Unlock === "true" || p.Unlock === "1" || p.Unlock === 1,
        View: p.View === true || p.View === "true" || p.View === "1" || p.View === 1,
      }))
    : [];

  return {
    name: decryptedData.diaplayname || decryptedData.diaplayname || "User",
    id: decryptedData.userid || decryptedData.usercode || "000000",
    email: decryptedData.empMail || decryptedData.email || "user@example.com",
    role: decryptedData.group_name || decryptedData.role || "User",
    phone: decryptedData.mobile_no || decryptedData.mobile_no || "",
    branch,
    branch_id: branch_id || null,
    branch_name: branch_name || null,
    branch_switching,
    employee_id:
      decryptedData.employee_id ||
      decryptedData.employeeId ||
      decryptedData.employeeid ||
      decryptedData.emp_id ||
      (decryptedData.loginUser &&
      typeof decryptedData.loginUser === "object" &&
      ((decryptedData.loginUser as any).employee_id ||
        (decryptedData.loginUser as any).employeeId ||
        (decryptedData.loginUser as any).employeeid ||
        (decryptedData.loginUser as any).emp_id)) ||
      undefined,
    userlevelpermissions,
    modulelevelpermissions: decryptedData.modulelevelpermissions || {},
  };
};

export const initializeAuth = createAsyncThunk<
  UserInfo | null,
  void,
  { state: RootState }
>("auth/initialize", async (_arg, { rejectWithValue, getState }) => {
  try {
    if (typeof window === "undefined") {
      return null;
    }

    const encryptedUserData = loadEncryptedUserData();
    if (!encryptedUserData) {
      return null;
    }

    const decryptedData = decryptUserData(encryptedUserData);
    if (!decryptedData) {
      return rejectWithValue("Failed to decrypt user data");
    }

    const {
      layout: { currentBranch },
    } = getState();

    return mapDecryptedDataToUser(decryptedData, currentBranch?.name);
  } catch (error) {
    console.error("initializeAuth error:", error);
    return rejectWithValue(
      error instanceof Error ? error.message : "Failed to initialize auth"
    );
  }
});

export const loginWithEncryptedData = createAsyncThunk<
  UserInfo,
  { encryptedData: string; fallbackBranch?: string }
>("auth/loginWithEncryptedData", async ({ encryptedData, fallbackBranch }) => {
  if (typeof window !== "undefined") {
    // console.log("encrypted<<>><<>><<>>Data", encryptedData);
    localStorage.setItem("encryptedData", encryptedData);
  }

  const decryptedData = authService.decryptData(encryptedData);
  console.log("decrypted<>><<<>><<<>>><<>>>><>>>Data", decryptedData);
  if (!decryptedData) {
    throw new Error("Failed to decrypt user data");
  }

  if (typeof window !== "undefined") {
    // Store session_id if present in decrypted data
    if (decryptedData.session_id) {
      localStorage.setItem("session_id", String(decryptedData.session_id));
    }

    try {
      const encryptedUserData = authService.encryptData(decryptedData);
      if (encryptedUserData) {
        localStorage.setItem("encryptedUserData", encryptedUserData);
        localStorage.removeItem("userData");
      }
    } catch (error) {
      console.warn("Unable to re-encrypt user data", error);
    }
  }
  console.log("decrypted<><>---<><><>Data", decryptedData)

  return mapDecryptedDataToUser(decryptedData, fallbackBranch);
});

const initialState: AuthState = {
  user: defaultUser,
  isAuthenticated: false,
  status: "idle",
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateUserBranch(state, action: PayloadAction<{ branch_id: string; branch_name: string }>) {
      state.user.branch_id = action.payload.branch_id;
      state.user.branch_name = action.payload.branch_name;
      state.user.branch = action.payload.branch_name;

      // Update encrypted user data in storage
      if (typeof window !== "undefined") {
        try {
          const encryptedUserData = localStorage.getItem("encryptedUserData");
          if (encryptedUserData) {
            const decryptedData = authService.decryptData(encryptedUserData);
            if (decryptedData && typeof decryptedData === "object") {
              // Update branch info in decrypted data
              decryptedData.branch_id = action.payload.branch_id;
              decryptedData.branch_name = action.payload.branch_name;
              decryptedData.branch = action.payload.branch_name;

              // Also update loginUser if it exists
              if (decryptedData.loginUser && typeof decryptedData.loginUser === "object") {
                (decryptedData.loginUser as any).branch_id = action.payload.branch_id;
                (decryptedData.loginUser as any).branch_name = action.payload.branch_name;
              }

              // Re-encrypt and save
              const updatedEncryptedData = authService.encryptData(decryptedData);
              if (updatedEncryptedData) {
                localStorage.setItem("encryptedUserData", updatedEncryptedData);
              }
            }
          }
        } catch (error) {
          console.error("Failed to update encrypted user data:", error);
        }
      }
    },
    logout(state) {
      state.user = defaultUser;
      state.isAuthenticated = false;
      state.status = "idle";
      state.error = undefined;

      if (typeof window !== "undefined") {
        localStorage.removeItem("encryptedData");
        localStorage.removeItem("encryptedUserData");
        localStorage.removeItem("userData");
        localStorage.removeItem("authToken");
        localStorage.removeItem("session_id"); // Clear session_id on logout
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload) {
          state.user = action.payload;
          state.isAuthenticated = true;

          // Load preferences when auth is restored from localStorage
          if (typeof window !== "undefined") {
            import("../../services/preferencesSync")
              .then(({ migrateLocalPreferencesToBackendOnce, loadPreferencesFromBackend }) => {
                migrateLocalPreferencesToBackendOnce()
                  .then(() => loadPreferencesFromBackend())
                  .then((prefs) => {
                    if (prefs) {
                      // Apply preferences to UI immediately
                      if (prefs.darkMode !== undefined) {
                        document.documentElement.classList.toggle("dark", prefs.darkMode);
                      }
                      if (prefs.theme) {
                        document.documentElement.setAttribute("data-theme", prefs.theme);
                      }
                    }
                  })
                  .catch((error) => {
                    console.warn("[Auth] Failed to load preferences after init:", error);
                  });
              })
              .catch((error) => {
                console.warn("[Auth] Failed to import preferences sync:", error);
              });
          }
        }
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string | undefined;
        state.isAuthenticated = false;
        state.user = defaultUser;
      })
      .addCase(loginWithEncryptedData.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(loginWithEncryptedData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload;
        state.isAuthenticated = true;

        // Load preferences after successful login
        if (typeof window !== "undefined") {
          import("../../services/preferencesSync")
            .then(({ migrateLocalPreferencesToBackendOnce, loadPreferencesFromBackend }) => {
              // Migrate first, then load
              migrateLocalPreferencesToBackendOnce()
                .then(() => loadPreferencesFromBackend())
                .then((prefs) => {
                  if (prefs) {
                    // Apply preferences to UI immediately
                    if (prefs.darkMode !== undefined) {
                      document.documentElement.classList.toggle("dark", prefs.darkMode);
                    }
                    if (prefs.theme) {
                      document.documentElement.setAttribute("data-theme", prefs.theme);
                    }
                  }
                })
                .catch((error) => {
                  console.warn("[Auth] Failed to load preferences after login:", error);
                });
            })
            .catch((error) => {
              console.warn("[Auth] Failed to import preferences sync:", error);
            });
        }
      })
      .addCase(loginWithEncryptedData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
        state.isAuthenticated = false;
        state.user = defaultUser;
      });
  },
});

export const { logout, updateUserBranch } = authSlice.actions;
export default authSlice.reducer;

